import { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { H6, Image } from "../../../AbstractElements";
import SvgIcon from "../../../Utils/CommonComponents/CommonIcons/CommonSvgIcons";
import { getFilteredPositionIds } from "../../../db/DexieServises";
import { API } from "../../../api/API";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { dynamicImage } from "../../../Utils";
import ActiveVacantTerritories from "../../Reports/AllKPIs/ActiveVacantTerritories";
import ClassificationChart from "../../Reports/AllKPIs/Classifications";
import TreeModelReport from "../Attendance/AttendanceTable/TreeModelReport";
import CardHeaderSpan from "../../../Utils/CommonComponents/CardHeaderSpan";
import RCPAsalesprescription from "../../Reports/AllKPIs/RCPAsalesprescription";

export default function CustomerCentral() {
  const positions = useSelector((state: RootState) => state.position.positions);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);

  const [averageRcpaOwnValue, setAverageRcpaOwnValue] = useState(0);
  const [averageRcpaCompValue, setAverageRcpaCompValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  // Local month range state
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [monthRange, setMonthRange] = useState<{
    from: { month: string; year: string };
    to: { month: string; year: string };
  }>({ from: { month: "", year: "" }, to: { month: "", year: "" } });
  const [appliedMonthRange, setAppliedMonthRange] = useState<{
    start: string;
    end: string;
  }>({ start: "", end: "" });

  const monthNumberToName: { [key: number]: string } = {
    1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
    7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
  };

  const monthToNumber: { [key: string]: number } = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = Array.from({ length: 2 }, (_, i) => currentYear - i);

  const toggleMonthModal = () => setMonthModalOpen(!monthModalOpen);

  const handleApplyDateRange = () => {
    if (monthRange.from.month && monthRange.from.year && monthRange.to.month && monthRange.to.year) {
      setAppliedMonthRange({
        start: `${monthToNumber[monthRange.from.month]}-${monthRange.from.year}`,
        end: `${monthToNumber[monthRange.to.month]}-${monthRange.to.year}`,
      });
    }
    toggleMonthModal();
  };

  // Default to last 3 months if no date range
  const getDefaultLast3Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
    }
    return months;
  };

  // Generate months between start and end for API calls
  const generateMonthsForApi = (start: string, end: string) => {
    const [startMonth, startYear] = start.split("-").map(Number);
    const [endMonth, endYear] = end.split("-").map(Number);

    const months: string[] = [];
    for (let year = startYear; year <= endYear; year++) {
      const startM = year === startYear ? startMonth : 1;
      const endM = year === endYear ? endMonth : 12;

      for (let month = startM; month <= endM; month++) {
        months.push(`${year}-${String(month).padStart(2, "0")}`);
      }
    }
    return months;
  };

  const calculateAverages = async () => {
    setLoading(true);
    const positionIds =
      selectedIds.length > 0
        ? selectedIds.join(",")
        : positions.length > 0
          ? positions.map((pos) => pos.id).join(",")
          : (await getFilteredPositionIds("Position", unitId)).join(",");

    const months =
      appliedMonthRange.start && appliedMonthRange.end
        ? generateMonthsForApi(appliedMonthRange.start, appliedMonthRange.end)
        : getDefaultLast3Months();

    let totalRcpaOwnValue = 0;
    let totalRcpaCompValue = 0;

    for (const month of months) {
      const serverUrl = localStorage.getItem("serverUrl");
      const url = `${serverUrl}${API.getEffortReport}`;

      const { status, data }: { status: number; data: { data: any[] } } =
        await getData(url, true, {
          position_id: positionIds,
          moye: month,
        });

      if (status === 200 && Array.isArray(data?.data)) {
        data.data.forEach((item: any) => {
          totalRcpaOwnValue += parseFloat(item.RcpaValueForOwnBrand || "0");
          totalRcpaCompValue += parseFloat(item.RcpaValueForCompBrand || "0");
        });
      }
    }

    setAverageRcpaOwnValue(totalRcpaOwnValue);
    setAverageRcpaCompValue(totalRcpaCompValue);
    setLoading(false);
  };

  useEffect(() => {
    calculateAverages();
  }, [positions, appliedMonthRange, selectedIds, unitId]);

  const formatValue = (value: number) => {
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  const getMonthRangeButtonLabel = () => {
    if (appliedMonthRange.start && appliedMonthRange.end) {
      const [startMonth, startYear] = appliedMonthRange.start.split("-");
      const [endMonth, endYear] = appliedMonthRange.end.split("-");
      return `${monthNumberToName[parseInt(startMonth, 10)]} ${startYear} - ${monthNumberToName[parseInt(endMonth, 10)]} ${endYear}`;
    }
    return "Select Month Range";
  };

  return (
    <div className="powerbi-dashboard-bg py-4 px-2 page-body">
      {/* Filter Section */}
      <div className="row px-3 mb-3 align-items-center">
        <div className=" d-flex align-items-end justify-content-between gap-1 flex-wrap">
          {/* <H6 className="mb-0"></H6> */}
            <CardHeaderSpan headingClassName='pb-0' back={true} heading={'Customer Central'} span={'Customer Central'} />
          <div className="d-flex gap-1">
          <TreeModelReport onSelectionChange={setSelectedIds} />
          <Button color="info" onClick={toggleMonthModal} size="sm">
            {getMonthRangeButtonLabel()}
          </Button>
          </div>
        </div>
      </div>

      {/* Month Range Modal */}
      <Modal isOpen={monthModalOpen} toggle={toggleMonthModal} centered>
        <ModalHeader toggle={toggleMonthModal}>Select Month Range</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <label>From</label>
              <select
                className="form-control"
                value={`${monthRange.from.month}-${monthRange.from.year}`}
                onChange={(e) => {
                  const [month, year] = e.target.value.split("-");
                  setMonthRange({ ...monthRange, from: { month, year } });
                }}
              >
                <option value="">Select Month</option>
                {yearOptions.flatMap((year) =>
                  Object.entries(monthNumberToName)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .filter(([monthNumber]) => {
                      if (year === currentYear) {
                        return Number(monthNumber) <= currentMonth;
                      }
                      return true;
                    })
                    .map(([_, monthName]) => (
                      <option
                        key={`from-${monthName}-${year}`}
                        value={`${monthName}-${year}`}
                      >
                        {`${monthName}-${year}`}
                      </option>
                    ))
                )}
              </select>
            </Col>
            <Col md={6}>
              <label>To</label>
              <select
                className="form-control"
                value={`${monthRange.to.month}-${monthRange.to.year}`}
                onChange={(e) => {
                  const [month, year] = e.target.value.split("-");
                  setMonthRange({ ...monthRange, to: { month, year } });
                }}
              >
                <option value="">Select Month</option>
                {yearOptions.flatMap((year) =>
                  Object.entries(monthNumberToName)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .filter(([monthNumber]) => {
                      if (year === currentYear) {
                        return Number(monthNumber) <= currentMonth;
                      }
                      return true;
                    })
                    .map(([_, monthName]) => (
                      <option
                        key={`to-${monthName}-${year}`}
                        value={`${monthName}-${year}`}
                      >
                        {`${monthName}-${year}`}
                      </option>
                    ))
                )}
              </select>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleMonthModal}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={() => {
              setMonthRange({ from: { month: "", year: "" }, to: { month: "", year: "" } });
              setAppliedMonthRange({ start: "", end: "" });
              toggleMonthModal();
            }}
          >
            Clear
          </Button>
          <Button color="success" onClick={handleApplyDateRange}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>

      {loading ? (
        <div className="text-center py-3">
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="d-flex mb-4">
            <div className="col-lg-4 col-md-6 col-sm-12 me-2">
              <Card style={{ minHeight: "120px", cursor: "pointer" }}>
                <CardBody
                  style={{
                    padding: "12px",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="customer-card d-flex justify-content-between align-items-center b-l-primary border-2"
                    style={{ display: "inline-block" }}
                  >
                    <div className="ms-3">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        RCPA Own vs Competitor
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "1rem",
                          margin: 0,
                          fontWeight: 600,
                        }}
                      >
                        ₹ {formatValue(averageRcpaOwnValue)}/
                        {formatValue(averageRcpaCompValue)}
                      </h3>
                    </div>
                    <div className={`dashboard-user bg-light-danger`}>
                      <span></span>
                      <SvgIcon
                        iconId={"money"}
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "4px",
                        }}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Charts */}
          <div className="mt-4">
            <Row className="g-4">
              <ActiveVacantTerritories />
              <ClassificationChart />
              <RCPAsalesprescription/>
            </Row>
          </div>
        </>
      )}
    </div>
  );
}
